import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Award, HandCoins, Loader2, MapPinned, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import RegionalDonutChart from "@/components/RegionalDonutChart";

const RUPIAH_FORMATTER = new Intl.NumberFormat("id-ID", {
  style: "currency",
  currency: "IDR",
  maximumFractionDigits: 0,
});

export default function AdminRecipientDashboard({ onViewParticipants }) {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    try {
      const response = await api.get("/admin/stats");
      setStats(response.data);
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const isRegionalAdmin = user?.role === "admin_wilayah";
  const distribution = useMemo(
    () => (isRegionalAdmin ? stats?.passed_by_subdistrict : stats?.passed_by_region) || [],
    [isRegionalAdmin, stats],
  );
  const geographyLabel = isRegionalAdmin ? "Kecamatan" : "Wilayah";

  if (!stats && !error) {
    return <LoadingState testId="recipient-dashboard-loading" />;
  }

  if (error) {
    return <ErrorState onRetry={load} testId="recipient-dashboard-error" />;
  }

  const passed = stats.verified || 0;
  const totalDisbursedAmount = Number(stats.total_disbursed_amount || 0);
  const tiles = [
    {
      id: "recipient-total",
      eyebrow: "Penerima Manfaat",
      label: "Total Penerima Manfaat",
      value: passed,
      icon: Award,
      accent: "#27AE60",
      iconClass: "bg-[#E8F6EE] text-[#0B6B3A]",
    },
    {
      id: isRegionalAdmin ? "recipient-subdistricts" : "recipient-regions",
      eyebrow: isRegionalAdmin ? "Cakupan Kecamatan" : "Cakupan Wilayah",
      label: `${geographyLabel} Penerima Manfaat`,
      value: distribution.length,
      icon: MapPinned,
      accent: "#F2C94C",
      iconClass: "bg-[#FFF8D8] text-[#8A6900]",
    },
    {
      id: "recipient-disbursed-funds",
      eyebrow: "Realisasi Pencairan",
      label: "Total Dana Sudah Dicairkan",
      value: RUPIAH_FORMATTER.format(totalDisbursedAmount),
      icon: HandCoins,
      accent: "#0B6B3A",
      iconClass: "bg-[#E8F6EE] text-[#0B6B3A]",
      valueTestId: "dashboard-recipient-disbursed-funds-value",
    },
  ];

  return (
    <div className="space-y-7" data-testid="recipient-dashboard">
      <div className="flex justify-end" data-testid="dashboard-data-year">
        <span className="rounded-full border border-[#B7E4C7] bg-[#F0FBF5] px-3 py-1.5 text-xs font-bold text-[#0B6B3A]">
          Data Tahun 2026
        </span>
      </div>
      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {tiles.map((tile) => {
          const Icon = tile.icon;
          return (
            <div
              key={tile.id}
              className="group overflow-hidden rounded-lg border border-gray-200 bg-white"
              data-testid={`dashboard-${tile.id}`}
            >
              <div className="flex items-center justify-between border-b border-gray-100 bg-[#F9FAFB] px-5 py-3">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#0B6B3A]">
                  {tile.eyebrow}
                </p>
                <span
                  className={`flex h-8 w-8 items-center justify-center rounded-md ${tile.iconClass}`}
                  aria-hidden="true"
                >
                  <Icon className="h-4 w-4" />
                </span>
              </div>
              <div className="flex items-end justify-between gap-4 px-5 py-5">
                <div>
                  <p className="text-sm font-semibold text-[#6B7280]">{tile.label}</p>
                  <p
                    className="mt-1 font-display text-3xl font-extrabold text-[#0B6B3A]"
                    data-testid={tile.valueTestId}
                  >
                    {tile.value}
                  </p>
                </div>
                <span
                  className="h-11 w-1 shrink-0 rounded-full"
                  style={{ backgroundColor: tile.accent }}
                  aria-hidden="true"
                />
              </div>
            </div>
          );
        })}
      </section>

      <RegionalDonutChart
        distribution={distribution}
        totalRecipients={passed}
        onViewParticipants={onViewParticipants}
        isRegionalAdmin={isRegionalAdmin}
        regionName={user?.region || ""}
      />
    </div>
  );
}

export function LoadingState({ testId }) {
  return (
    <div className="py-16 text-center" data-testid={testId}>
      <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#27AE60]" />
    </div>
  );
}

export function ErrorState({ onRetry, testId }) {
  return (
    <div className="border border-[#FECACA] bg-[#FEF2F2] p-6 text-center" data-testid={testId}>
      <p className="text-sm font-semibold text-[#991B1B]">Data Dashboard belum dapat dimuat.</p>
      <button
        type="button"
        onClick={onRetry}
        data-testid={`${testId}-retry`}
        className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-[#B91C1C] hover:underline"
      >
        <RefreshCw className="h-4 w-4" />
        Coba lagi
      </button>
    </div>
  );
}